/**
 * assets/nl-categories-map.js — Nisa Lab illustrated scroll-driven category map.
 *
 * Loads GSAP + ScrollTrigger from a CDN on demand (shared across instances),
 * pins the section and scrubs a horizontal track against vertical scroll on
 * desktop, hands off to native touch scrolling under 901px, and skips all
 * scroll-jacking / zoom theatrics when the visitor has requested reduced
 * motion (section then renders as a static wrapped row via CSS only).
 *
 * Hooks into Shopify's theme editor section lifecycle so re-ordering or
 * editing this section in the customizer doesn't leak duplicate
 * ScrollTriggers or leave a stale pin behind.
 */

const GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
const SCROLLTRIGGER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

let gsapLoadPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', reject, { once: true });
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
}

function loadGSAP() {
  if (window.gsap && window.ScrollTrigger) return Promise.resolve();
  if (!gsapLoadPromise) {
    gsapLoadPromise = loadScript(GSAP_URL)
      .then(() => loadScript(SCROLLTRIGGER_URL))
      .then(() => {
        window.gsap.registerPlugin(window.ScrollTrigger);
      });
  }
  return gsapLoadPromise;
}

const instances = new WeakMap();

function initMap(root) {
  if (!root || instances.has(root)) return;

  const track = root.querySelector('[data-nl-map-track]');
  const items = Array.from(root.querySelectorAll('[data-nl-map-item]'));
  const hint = root.querySelector('[data-nl-map-hint]');
  const overlay = root.querySelector('[data-nl-map-overlay]');
  if (!track || !items.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const state = { mm: null, isNavigating: false };
  instances.set(root, state);

  if (prefersReducedMotion) {
    root.classList.add('nl-map--static');
    return;
  }

  loadGSAP().then(() => {
    if (!instances.has(root)) return; // section was unloaded while GSAP was still loading
    setupScroll(root, track, hint, state);
    setupHoverAndClick(items, overlay, state);
  });
}

function setupScroll(root, track, hint, state) {
  const { gsap } = window;
  state.mm = gsap.matchMedia();

  state.mm.add('(min-width: 901px)', () => {
    const getDistance = () => track.scrollWidth - window.innerWidth;

    const tween = gsap.to(track, {
      x: () => -getDistance(),
      ease: 'none',
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: () => `+=${getDistance()}`,
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (hint) gsap.to(hint, { opacity: self.progress > 0.05 ? 0 : 0.75, duration: 0.3, overwrite: 'auto' });
        }
      }
    });

    return () => {
      tween.scrollTrigger && tween.scrollTrigger.kill();
      tween.kill();
      gsap.set(track, { clearProps: 'transform' });
    };
  });

  state.mm.add('(max-width: 900px)', () => {
    root.classList.add('nl-map--touch');
    if (hint) gsap.set(hint, { opacity: 0.75 });
    return () => root.classList.remove('nl-map--touch');
  });
}

function setupHoverAndClick(items, overlay, state) {
  const { gsap } = window;

  items.forEach((item) => {
    const img = item.querySelector('.nl-map__img');
    const sign = item.querySelector('[data-nl-map-sign]');
    if (sign) gsap.set(sign, { opacity: 0, y: 18, scale: 0.85, transformOrigin: '50% 100%' });

    const playHover = () => {
      if (state.isNavigating) return;
      if (img) gsap.to(img, { scale: 1.08, duration: 0.5, ease: 'power2.out' });
      if (sign) gsap.to(sign, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: 'back.out(1.7)' });
    };
    const reverseHover = () => {
      if (state.isNavigating) return;
      if (img) gsap.to(img, { scale: 1, duration: 0.5, ease: 'power2.out' });
      if (sign) gsap.to(sign, { opacity: 0, y: 18, scale: 0.85, duration: 0.3, ease: 'power1.in' });
    };

    item.addEventListener('mouseenter', playHover);
    item.addEventListener('mouseleave', reverseHover);
    item.addEventListener('focus', playHover);
    item.addEventListener('blur', reverseHover);
    item.addEventListener('click', (event) => onItemClick(event, item, img, items, overlay, state));
  });
}

function onItemClick(event, item, img, items, overlay, state) {
  const url = item.getAttribute('href');
  if (!url || url === '#') {
    event.preventDefault();
    return;
  }
  // Let modifier-clicks / middle-clicks open a new tab natively instead of hijacking them.
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (state.isNavigating) {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  state.isNavigating = true;

  const { gsap } = window;
  const zoomOrigin = item.dataset.zoomOrigin || '50% 60%';

  items.forEach((other) => {
    if (other !== item) gsap.to(other, { opacity: 0, duration: 0.5, ease: 'power1.in' });
  });

  const sign = item.querySelector('[data-nl-map-sign]');
  if (sign) gsap.to(sign, { opacity: 0, duration: 0.2 });

  if (!img && !overlay) {
    window.location.href = url;
    return;
  }

  const tl = gsap.timeline({
    onComplete: () => {
      window.location.href = url;
    }
  });

  if (img) {
    tl.to(img, { scale: 2.4, transformOrigin: zoomOrigin, duration: 0.9, ease: 'power2.inOut' }, 0);
  }
  if (overlay) {
    tl.to(overlay, { opacity: 1, duration: 0.55, ease: 'power1.in' }, 0.4);
  }
}

function destroyMap(root) {
  const state = instances.get(root);
  if (!state) return;
  if (state.mm) state.mm.revert();
  instances.delete(root);
  root.classList.remove('nl-map--touch', 'nl-map--static');
}

function initAll(scope) {
  (scope || document).querySelectorAll('[data-nl-map]').forEach(initMap);
}

initAll(document);

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-nl-map]').forEach(destroyMap);
});
